/**
 * Разбор ошибок API: бэк возвращает тело вида
 * { module, code, message, timestamp, retryAfter, debug }.
 *
 * fetchApi пробрасывает исходный AxiosError, поэтому тело доступно
 * в error.response.data, а error.message — это техническое
 * «Request failed with status code 400», которое пользователю показывать нельзя.
 */

export const API_ERROR_CODE = {
  /**
   * Маркировку ОРД у креатива нельзя отключить после включения: креатив уже
   * передан в ОРД, и повторная регистрация создала бы дубли.
   */
  CREATIVE_ORD_MARK_LOCKED: 1455,
  /**
   * Креатив пустой: нет ни текста (content.text), ни вложений
   * (content.mediaItems), ни ссылки для репоста (content.url).
   * Частый случай — вложение не загрузилось и бэк не получил ссылку на файл.
   */
  CREATIVE_CONTENT_EMPTY: 1456,
  /**
   * Публикацию уже взяли в работу — паблишер пытается принять заказ,
   * который к этому моменту забрал другой канал или другая вкладка.
   */
  PUBLICATION_ALREADY_TAKEN: 1595,
  /**
   * Канал нельзя отвязать: на нём остались незавершённые публикации.
   * Владелец должен сначала довести их до конца.
   */
  PLACE_HAS_UNFINISHED_PUBLICATIONS: 2410,
  /**
   * Автопостинг невозможен: файл в посте больше лимита Telegram на загрузку.
   * Бот тут ни при чём — показываем текст бэка, в нём размер файла и лимит.
   */
  AUTOPOST_FILE_TOO_LARGE: 3116,
  /**
   * Привязку мессенджера запрашивают чаще раза в 30 секунд.
   * Отдаётся на /user/messenger/bot/user-bind-start.
   */
  MESSENGER_BIND_TOO_OFTEN: 1307,
  /**
   * У обеих учёток есть данные, поэтому автоматически объединить их нельзя.
   * Приезжает через check-auth: привязка завершается в колбэке бота.
   */
  MESSENGER_MERGE_NOT_ALLOWED: 1353,
  /**
   * Бэк не знает такого значения messenger — на клиенте это означает,
   * что мы отправили что-то помимо telegram и max.
   */
  MESSENGER_UNKNOWN: 1354,

  // ===== Установка и подтверждение почты =====
  /** Почта уже установлена: set-email-request и email-confirm ей недоступны. */
  EMAIL_ALREADY_SET: 1301,
  /** Запроса на подтверждение нет или он истёк — нужен новый код. */
  EMAIL_CONFIRM_EXPIRED: 1302,
  /** Указан адрес, который уже стоит у этого же аккаунта. */
  EMAIL_SAME_AS_CURRENT: 1311,
  /** Менять нечего: подтверждённой почты у аккаунта нет, её можно только установить. */
  EMAIL_NOT_CONFIRMED: 1214,
  /** Код введён неверно. */
  EMAIL_CODE_INVALID: 1344,
  /** Попытки ввода кода исчерпаны. */
  EMAIL_CODE_ATTEMPTS_EXCEEDED: 1345,
  /** Повторный запрос кода чаще раза в минуту. */
  EMAIL_CODE_TOO_OFTEN: 1346,
  /** Код должен состоять из шести цифр. */
  EMAIL_CODE_FORMAT: 1347,
} as const;

// Тексты для кодов, которые нужно показать пользователю по-русски.
const MESSAGES_BY_CODE: Record<number, string> = {
  [API_ERROR_CODE.CREATIVE_ORD_MARK_LOCKED]:
    "Креатив нельзя обновить из-за ограничений ОРД: маркировку для него уже включили, и отключить её нельзя. Верните маркировку, чтобы сохранить изменения.",
  [API_ERROR_CODE.CREATIVE_CONTENT_EMPTY]:
    "Пост пустой: добавьте текст, вложение или ссылку для репоста. Если вы прикрепляли файл — возможно, он не загрузился, попробуйте прикрепить его заново.",
  [API_ERROR_CODE.PUBLICATION_ALREADY_TAKEN]:
    "Заказ уже взят в работу. Обновите список заказов.",
  [API_ERROR_CODE.MESSENGER_BIND_TOO_OFTEN]:
    "Мы уже отправили ссылку на привязку. Повторить можно через полминуты.",
  // ТЗ: «нужно показать текст про ручное объединение и поддержку»
  [API_ERROR_CODE.MESSENGER_MERGE_NOT_ALLOWED]:
    "Аккаунт уже содержит данные. Для объединения обратитесь в поддержку:",
  [API_ERROR_CODE.MESSENGER_UNKNOWN]: "Этот мессенджер пока не поддерживается.",

  [API_ERROR_CODE.EMAIL_ALREADY_SET]:
    "У аккаунта уже указана почта — её можно только сменить.",
  [API_ERROR_CODE.EMAIL_CONFIRM_EXPIRED]:
    "Срок действия кода истёк. Запросите новый код.",
  [API_ERROR_CODE.EMAIL_SAME_AS_CURRENT]: "Это ваша текущая почта. Укажите другой адрес.",
  [API_ERROR_CODE.EMAIL_NOT_CONFIRMED]:
    "У аккаунта нет подтверждённой почты — её можно только установить.",
  [API_ERROR_CODE.EMAIL_CODE_INVALID]: "Код введён неверно.",
  [API_ERROR_CODE.EMAIL_CODE_ATTEMPTS_EXCEEDED]:
    "Попытки ввода кода исчерпаны. Запросите новый код.",
  [API_ERROR_CODE.EMAIL_CODE_TOO_OFTEN]:
    "Новый код можно запросить не чаще раза в минуту.",
  [API_ERROR_CODE.EMAIL_CODE_FORMAT]: "Код состоит из шести цифр.",
};

/**
 * Тело ошибки бэка. Достаём из двух форматов:
 *   - axios (baseApi)  → error.response.data
 *   - simpleApi        → кладёт code/data на сам Error при throw
 */
export const getApiErrorData = (error: any): any =>
  error?.response?.data ?? error?.data ?? (typeof error?.code === "number" ? error : null);

export const getApiErrorCode = (error: any): number | null => {
  const code = getApiErrorData(error)?.code;
  return typeof code === "number" ? code : null;
};

export const isApiErrorCode = (error: any, code: number): boolean =>
  getApiErrorCode(error) === code;

/**
 * Объединение учёток вручную. Один и тот же случай приходит и как 1353,
 * и как HTTP 409 — и на set-email-request, и на email-confirm, если данные
 * появились между отправкой кода и подтверждением.
 */
export const MERGE_NOT_ALLOWED_MESSAGE =
  "Обе учётные записи содержат данные — объединить их автоматически нельзя.";

export const isMergeNotAllowed = (error: any) =>
  getApiErrorCode(error) === API_ERROR_CODE.MESSENGER_MERGE_NOT_ALLOWED ||
  error?.statusCode === 409 ||
  error?.response?.status === 409;

/**
 * Человекочитаемый текст ошибки для пользователя.
 * Приоритет: наш текст по коду → сообщение бэка, если оно на русском → fallback.
 * Английские сообщения бэка не показываем.
 */
export const getApiErrorMessage = (error: any, fallback: string): string => {
  const code = getApiErrorCode(error);
  if (code !== null && MESSAGES_BY_CODE[code]) return MESSAGES_BY_CODE[code];

  const message = getApiErrorData(error)?.message;
  if (typeof message === "string" && /[а-яё]/i.test(message)) return message;

  return fallback;
};
